import React, { useRef, useEffect } from 'react';

// Define the colors for different states
const stateColors = {
  normal: "#71E171",   // Green for normal
  critical: "#E13131", // Red for critical
  noData: "#3131E1",   // Blue for no data
  missing: "#000000"   // Black for missing values
};

// Canvas component to visualize the stations without a legend and with compact spacing
function StationChainCanvas({ stations }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas width to the parent container width
    const parentWidth = canvas.parentElement.offsetWidth; // Get parent element width
    canvas.width = parentWidth; // Set canvas width dynamically
    canvas.height = 150; // Reduced height to compactly fit both rows

    const width = canvas.width;
    const height = canvas.height;

    // Clear the canvas
    context.clearRect(0, 0, width, height);
    
    const circleRadius = 7; // Radius of each circle
    const padding = 40; // Adjusted padding between circles to avoid clipping text
    const totalCircles = stations.length;
    
    // Calculate the available width for circles and distances between them
    const availableWidth = width - (2 * padding);
    const spaceBetweenCircles = availableWidth / (totalCircles - 1);

    // Set font for station names
    context.font = "14px Arial";
    context.textAlign = "left";  // Align left to position the text based on the first letter

    // Adjusted Y positions for the two rows to avoid squishing
    const yPositionLevel = height / 3; // Adjust position for the levelState row (upper row)
    const yPositionFlow = (2 * height) / 3; // Adjust position for the flowState row (lower row)

    // Draw circles for levelState and station names for each station along the X-axis
    stations.forEach((station, index) => {
      const xPosition = padding + index * spaceBetweenCircles;

      // LEVEL STATE CIRCLES
      if (station.hasLevel) {
        const fillColorLevel = stateColors[station.levelState] || stateColors.missing; // Default to black if undefined
        context.beginPath();
        context.arc(xPosition, yPositionLevel, circleRadius, 0, 2 * Math.PI);
        context.fillStyle = fillColorLevel;
        context.fill();
      } else {
        context.beginPath();
		context.arc(xPosition, yPositionLevel, circleRadius, 0, 2 * Math.PI);
		context.fillStyle = stateColors.missing; // Black color for missing data
		context.fill();

		// Draw the "X" over the circle
		context.beginPath();
		context.moveTo(xPosition - circleRadius * 0.7, yPositionLevel - circleRadius * 0.7); // Top-left to bottom-right
		context.lineTo(xPosition + circleRadius * 0.7, yPositionLevel + circleRadius * 0.7);

		context.moveTo(xPosition + circleRadius * 0.7, yPositionLevel - circleRadius * 0.7); // Top-right to bottom-left
		context.lineTo(xPosition - circleRadius * 0.7, yPositionLevel + circleRadius * 0.7);

		context.strokeStyle = "#FFFFFF"; // White color for X
		context.lineWidth = 2; // Adjust thickness of X
		context.stroke();
        context.font = "10px Arial"; // Adjust font size for X
      }

      // FLOW STATE CIRCLES
      if (station.hasFlow) {
        const fillColorFlow = stateColors[station.flowState] || stateColors.missing; // Default to black if undefined
        context.beginPath();
        context.arc(xPosition, yPositionFlow, circleRadius, 0, 2 * Math.PI);
        context.fillStyle = fillColorFlow;
        context.fill();
      } else {
        // Draw a black circle with an "X" if flow data is missing
        context.beginPath();
		context.arc(xPosition, yPositionFlow, circleRadius, 0, 2 * Math.PI);
		context.fillStyle = stateColors.missing; // Black color for missing data
		context.fill();
		context.fillStyle = "#FFFFFF"; // White color for X

		// Draw the "X" over the circle
		context.beginPath();
		context.moveTo(xPosition - circleRadius * 0.7, yPositionFlow - circleRadius * 0.7); // Top-left to bottom-right
		context.lineTo(xPosition + circleRadius * 0.7, yPositionFlow + circleRadius * 0.7);

		context.moveTo(xPosition + circleRadius * 0.7, yPositionFlow - circleRadius * 0.7); // Top-right to bottom-left
		context.lineTo(xPosition - circleRadius * 0.7, yPositionFlow + circleRadius * 0.7);

		context.strokeStyle = "#FFFFFF"; // White color for X
		context.lineWidth = 2; // Adjust thickness of X
		context.stroke();
      }

      // Detect if canvas width is less than 700, then rotate text by 30 degrees counterclockwise
      if (width < 700) {
        context.save(); // Save the current state of the canvas before applying the rotation

        // Translate to position the first letter directly above the circle
        context.translate(xPosition, yPositionLevel - circleRadius - 10);

        // Rotate the text by -30 degrees (counterclockwise)
        context.rotate((-30 * Math.PI) / 180);

        context.fillStyle = "#000"; // Set text color to black
        const displayStationName = station.stationName.length > 6 
		  ? station.stationName.slice(0, 6) + "..." 
		  : station.stationName;

		// Draw the (possibly truncated) station name
		context.fillText(displayStationName, 0, 0);
        context.restore(); // Restore the original state of the canvas after the rotation
      } else {
        // Rotate the text by 15 degrees counterclockwise for larger screens (width >= 700)
        context.save(); // Save the current state of the canvas before applying the rotation
        if (index < totalCircles - 1) {
        	context.translate(xPosition - 9, yPositionLevel - circleRadius - 10);
        } else {
        	context.translate(xPosition - 9, yPositionLevel - circleRadius - 10);
        }
        context.rotate((-15 * Math.PI) / 180); // Rotate the text by -15 degrees (counterclockwise)

        context.fillStyle = "#000"; // Set text color to black
        const displayStationName = station.stationName.length > (index < totalCircles - 1 ? 16 : 7) 
		  ? station.stationName.slice(0, (index < totalCircles - 1 ? 16 : 7)) + "..." 
		  : station.stationName;

		// Draw the (possibly truncated) station name
		context.fillText(displayStationName, 0, 0);
        context.restore(); // Restore the original state of the canvas after the rotation
      }

      // Draw the connecting line between levelState circles, if not the last one
      if (index < totalCircles - 1) {
        const nextXPosition = padding + (index + 1) * spaceBetweenCircles;
        // Level State Line
        context.beginPath();
        context.moveTo(xPosition + circleRadius, yPositionLevel);
        context.lineTo(nextXPosition - circleRadius, yPositionLevel);
        context.strokeStyle = "#000000"; // Line color (black)
        context.lineWidth = 2;
        context.stroke();

        // Flow State Line
        context.beginPath();
        context.moveTo(xPosition + circleRadius, yPositionFlow);
        context.lineTo(nextXPosition - circleRadius, yPositionFlow);
        context.strokeStyle = "#000000"; // Line color (black)
        context.lineWidth = 2;
        context.stroke();
      }
    });

    // Draw "H" label left of the top series of circles, rotated 90 degrees anticlockwise
    context.save();
    context.translate(padding - 20, yPositionLevel + 3); // Translate to the position just left of the first series
    context.fillStyle = "#000"; // Set text color to black
    context.fillText("H", 0, 0); // Draw "H"
    context.restore();

    // Draw "Q" label left of the second series of circles, rotated 90 degrees anticlockwise
    context.save();
    context.translate(padding - 20, yPositionFlow + 3); // Translate to the position just left of the second series
    context.fillStyle = "#000"; // Set text color to black
    context.fillText("Q", 0, 0); // Draw "Q"
    context.restore();

    // Draw legend at the bottom center of the canvas
    context.textAlign = "center";
    context.font = "12px Arial";
    context.fillStyle = "#000"; // Set text color to black
    context.fillText("Series H - Water Level Status     |     Series Q - Flow Rate Status", width / 2, height - 10);

  }, [stations]); // Redraw the canvas whenever stations data changes

  return (
    <canvas 
      ref={canvasRef} 
      style={{ 
        width: "100%", // Canvas width set to 100% for responsive behavior
        height: "150px", // Reduced height to fit both rows of circles without squishing
        marginTop: "2px", 
        marginBottom: "2px", 
        border: "1px solid grey", 
        borderRadius: "15px" // Rounded corners for the canvas
      }} 
    />
  );
}

export default StationChainCanvas;
